"use client"

import * as React from "react"
import { InstalledApp } from "@/types/workspace"

export function useHomeScreenState({
  mobileHomeOrder,
  setMobileHomeOrder,
  widgetsConfig,
  installedApps,
  hiddenAppIds,
  theme,
  desktopWallpaper,
  mobileWallpaper,
  useSeparateMobileWallpaper
}: {
  mobileHomeOrder: string[]
  setMobileHomeOrder: React.Dispatch<React.SetStateAction<string[]>>
  widgetsConfig: any[]
  installedApps: InstalledApp[]
  hiddenAppIds: string[]
  theme: string | undefined
  desktopWallpaper: string
  mobileWallpaper: string
  useSeparateMobileWallpaper: boolean
}) {
  const [mobileSearch, setMobileSearch] = React.useState("")
  const [draggedHomeKey, setDraggedHomeKey] = React.useState<string | null>(null)
  const [draggedAppId, setDraggedAppId] = React.useState<string | null>(null)

  const finalMobileHomeOrder = React.useMemo(() => {
    const order = [...mobileHomeOrder]

    widgetsConfig.forEach((w) => {
      const key = `widget:${w.id}`
      if (w.enabled && !order.includes(key)) {
        order.push(key)
      }
    })

    installedApps.forEach((ia) => {
      const { app } = ia
      const key = `app:${app.id}`
      if (ia.status === "active" && !hiddenAppIds.includes(app.id) && !order.includes(key)) {
        order.push(key)
      }
    })

    return order.filter((key) => {
      if (key.startsWith("widget:")) {
        const wid = key.replace("widget:", "")
        const w = widgetsConfig.find((item) => item.id === wid)
        return !!w && w.enabled
      }
      if (key.startsWith("app:")) {
        const aid = key.replace("app:", "")
        const installed = installedApps.find((ia) => ia.app.id === aid)
        return !!installed && installed.status === "active" && !hiddenAppIds.includes(aid)
      }
      return false
    })
  }, [mobileHomeOrder, installedApps, hiddenAppIds, widgetsConfig])

  const moveHomeItem = React.useCallback(
    (key: string, direction: "left" | "right") => {
      const orderList = [...finalMobileHomeOrder]
      const index = orderList.indexOf(key)
      if (index === -1) return

      if (direction === "left" && index > 0) {
        const temp = orderList[index]
        orderList[index] = orderList[index - 1]
        orderList[index - 1] = temp
      } else if (direction === "right" && index < orderList.length - 1) {
        const temp = orderList[index]
        orderList[index] = orderList[index + 1]
        orderList[index + 1] = temp
      }

      setMobileHomeOrder(orderList)
    },
    [finalMobileHomeOrder, setMobileHomeOrder]
  )

  const handleHomeDragStart = React.useCallback((e: React.DragEvent, key: string) => {
    setDraggedHomeKey(key)
    e.dataTransfer.effectAllowed = "move"
  }, [])

  const handleHomeDragOver = React.useCallback((e: React.DragEvent) => {
    e.preventDefault()
  }, [])

  const handleHomeDrop = React.useCallback(
    (e: React.DragEvent, targetKey: string) => {
      e.preventDefault()
      if (!draggedHomeKey || draggedHomeKey === targetKey) return

      const orderList = [...finalMobileHomeOrder]
      const draggedIndex = orderList.indexOf(draggedHomeKey)
      const targetIndex = orderList.indexOf(targetKey)

      if (draggedIndex === -1 || targetIndex === -1) return

      orderList.splice(draggedIndex, 1)
      orderList.splice(targetIndex, 0, draggedHomeKey)

      setMobileHomeOrder(orderList)
    },
    [draggedHomeKey, finalMobileHomeOrder, setMobileHomeOrder]
  )

  const desktopWallpaperStyle = React.useMemo(() => {
    const isLight = theme === "light"
    const getBg = () => {
      if (!desktopWallpaper || desktopWallpaper === "bold-tech") {
        return `url("/bold-tech-wallpaper.png")`
      }
      if (desktopWallpaper === "bold-light") {
        return `url("/bold-tech-wallpaper.png")`
      }
      if (desktopWallpaper === "gradient-dark") {
        return "linear-gradient(to bottom right, #18181b, #0f172a, #090d16)"
      }
      if (desktopWallpaper === "sunset") return "linear-gradient(to top right, #881337, #311042, #0f172a)"
      if (desktopWallpaper === "aurora") return "linear-gradient(to bottom right, #064e3b, #18181b, #1e1b4b)"
      if (desktopWallpaper.startsWith("http") || desktopWallpaper.startsWith("data:") || desktopWallpaper.startsWith("/")) {
        return `url("${desktopWallpaper}")`
      }
      return desktopWallpaper
    }
    return {
      backgroundImage: getBg(),
      backgroundSize: "cover",
      backgroundPosition: "center",
      backgroundRepeat: "no-repeat"
    }
  }, [desktopWallpaper, theme])

  const mobileWallpaperStyle = React.useMemo(() => {
    const source = useSeparateMobileWallpaper ? mobileWallpaper : desktopWallpaper
    const getBg = () => {
      if (!source || source === "bold-tech") {
        return `url("/bold-tech-wallpaper.png")`
      }
      if (source === "bold-light") {
        return `url("/bold-tech-wallpaper.png")`
      }
      if (source === "gradient-dark") {
        return "linear-gradient(to bottom right, #18181b, #0f172a, #090d16)"
      }
      if (source === "sunset") return "linear-gradient(to top right, #881337, #311042, #0f172a)"
      if (source === "aurora") return "linear-gradient(to bottom right, #064e3b, #18181b, #1e1b4b)"
      if (source.startsWith("http") || source.startsWith("data:") || source.startsWith("/")) {
        return `url("${source}")`
      }
      return source
    }
    return {
      backgroundImage: getBg(),
      backgroundSize: "cover",
      backgroundPosition: "center",
      backgroundRepeat: "no-repeat"
    }
  }, [mobileWallpaper, desktopWallpaper, useSeparateMobileWallpaper, theme])

  return {
    mobileSearch,
    setMobileSearch,
    draggedAppId,
    setDraggedAppId,
    finalMobileHomeOrder,
    moveHomeItem,
    handleHomeDragStart,
    handleHomeDragOver,
    handleHomeDrop,
    desktopWallpaperStyle,
    mobileWallpaperStyle
  }
}
